import { useMemo, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { CircleCheck, CircleMinus, FilePlus, FileText } from 'lucide-react'

import type { QuoteEmailOverride } from '@/gen/cortado/v1/cortado_pb'
import type { Inquiry } from '@/gen/cortado/v1/inquiries_pb'
import type { QuoteLite } from '@/gen/cortado/v1/invoices_pb'
import { QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { useQuoteSend } from '@/hooks/use-quote-send'
import {
  useContactQuoteInteractionsByQuoteIds,
  useQuoteLitesByIds,
  useQuotesByIds,
  type QuoteByIdState,
} from '@/stores/quotes.store'
import { useInvalidateJobAfterQuoteChange } from '@/stores/pipeline.store'
import { formatCurrency } from '@/lib/currency-utils'
import { cn, formatFullName } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { QuoteStatusBadge } from '@/components/shared/enum-badge'
import { AnimatedCollapse, CollapseChevron } from '@/components/shared/animated-collapse'
import { CardSummaryItems, type CardSummaryItem } from '@/components/pipeline/card-summary-items'
import { QuoteSendDialog } from '@/components/quotes/quote-send-dialog'
import { getQuoteRejectionReasonLabel } from '@/components/quotes/quote-rejection'
import {
  jobDetailQuoteEventsSummary,
  jobDetailSortQuotes,
  jobDetailWithTipText,
  type QuoteEventSummaryToken,
} from '@/components/pipeline/job-detail-view'
import {
  jobQuoteTileFooter,
  jobQuoteTileStatusLabel,
  jobQuoteTimeline,
  jobQuoteValidUntilText,
  jobQuoteViewSummaries,
  type JobQuoteTimelineStep,
  type JobQuoteViewSummary,
  type JobQuoteTimelineTone,
} from '@/components/pipeline/job-quote-tile'

interface JobQuotesProps {
  /** The job the quotes belong to; each tile opens its quote under this job. */
  jobId: string
  quoteIds: string[]
  acceptedQuoteId?: string
  /** The job's inquiry — its events are the request each quote is compared to, its contact the reminder's recipient. */
  inquiry: Inquiry | undefined
  /**
   * Starts a quote drafted from the inquiry. When set (a lead), a job with no
   * quotes shows a "No quote yet" card offering it.
   */
  onDraftQuote?: () => void
  /** The header's "+ New quote" menu. A job with no quotes still shows the card when it's set. */
  newQuoteAction?: ReactNode
  /**
   * Booked or complete: the quotes are history, so the card starts collapsed
   * even when no quote on the job is marked accepted.
   */
  isBooked?: boolean
}

/** A summary part; parts that differ from the request get a quiet highlight and a "Request: …" tooltip. */
function SummaryToken({ token }: { token: QuoteEventSummaryToken }) {
  if (!token.requestTooltip) return <span>{token.text}</span>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={`${token.text} (${token.requestTooltip})`}
          className="rounded-sm font-medium text-foreground underline decoration-dotted underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          {token.text}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{token.requestTooltip}</p>
      </TooltipContent>
    </Tooltip>
  )
}

const DOT_CLASS: Record<JobQuoteTimelineTone, string> = {
  reached: 'bg-status-success-fg',
  pending: 'border border-muted-foreground/60',
  declined: 'bg-status-danger-fg',
  expired: 'bg-status-danger-fg/60',
}
const STEP_TEXT_CLASS: Record<JobQuoteTimelineTone, string> = {
  reached: 'text-status-success-fg',
  pending: 'text-muted-foreground',
  declined: 'text-status-danger-fg',
  expired: 'text-muted-foreground',
}

/** "● Sent Oct 12 — ● Viewed Oct 13 — ○ Expires Oct 19", wrapping on narrow tiles. */
function QuoteTimeline({ label, steps }: { label: string; steps: JobQuoteTimelineStep[] }) {
  return (
    <ol aria-label={`${label} timeline`} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      {steps.map((step, index) => (
        <li key={step.key} data-tone={step.tone} className={cn('flex items-center gap-1.5', STEP_TEXT_CLASS[step.tone])}>
          {index > 0 && <span aria-hidden className="mr-0.5 h-px w-3 bg-border" />}
          <span aria-hidden className={cn('size-2 shrink-0 rounded-full', DOT_CLASS[step.tone])} />
          <span>{step.label}</span>
        </li>
      ))}
    </ol>
  )
}

interface QuoteReminderButtonProps {
  quoteId: string
  quoteLabel: string
  organizationId: string
  recipientName: string
  recipientEmail: string
}

/**
 * "Send reminder": opens the quote send dialog as a resend. It never sends on
 * click; the user reviews the email and sends from the dialog. The dialog
 * mounts on first open so tiles don't each load the email template.
 */
function QuoteReminderButton({ quoteId, quoteLabel, organizationId, recipientName, recipientEmail }: QuoteReminderButtonProps) {
  const [open, setOpen] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)
  const invalidateJob = useInvalidateJobAfterQuoteChange()
  const { sendQuote, isSending } = useQuoteSend({ isDraft: false, onSuccess: () => void invalidateJob() })

  // The dialog closes itself unless this resolves false (the send failed).
  function handleSend(input: { additionalRecipientEmails: string[]; emailOverride: QuoteEmailOverride }) {
    return sendQuote({ quoteId, ...input })
  }

  return (
    <>
      <button
        type="button"
        aria-label={`Send reminder for ${quoteLabel}`}
        className="rounded-sm text-xs text-muted-foreground underline-offset-2 outline-none hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring/50"
        onClick={() => {
          setHasOpened(true)
          setOpen(true)
        }}
      >
        Send reminder
      </button>
      {hasOpened && (
        <QuoteSendDialog
          open={open}
          onOpenChange={setOpen}
          organizationId={organizationId}
          quoteId={quoteId}
          isDraft={false}
          recipientName={recipientName}
          recipientEmail={recipientEmail}
          isSending={isSending}
          onSend={handleSend}
        />
      )}
    </>
  )
}

interface JobQuoteTileProps {
  jobId: string
  lite: QuoteLite
  full: QuoteByIdState | undefined
  inquiry: Inquiry | undefined
  /** The client's views of this quote, once the interactions load. */
  views: JobQuoteViewSummary | undefined
}

/**
 * One quote as a tile: number and status, total, what it proposes, how far
 * it's got with the client, and what to do next. Renders from the lite at
 * once; the full quote fills in the events and the viewed/accepted/declined
 * dates.
 */
function JobQuoteTile({ jobId, lite, full, inquiry, views }: JobQuoteTileProps) {
  const quote = full?.quote
  const status = quote?.status ?? lite.status
  const label = `Quote #${lite.quoteNumber}`
  const isRejected = status === QuoteStatusEnum.QUOTE_STATUS_REJECTED
  const summary = quote ? jobDetailQuoteEventsSummary(quote.quoteEvents, inquiry?.events) : undefined
  const totalCents = quote?.totalCents ?? lite.totalCents
  const withTip = jobDetailWithTipText(totalCents, quote?.totalWithTipCents ?? lite.totalWithTipCents)
  const validUntil = quote?.validUntil ?? lite.validUntil
  const requiresQuestionnaire = quote?.requiresQuestionnaire ?? lite.requiresQuestionnaire
  const validUntilText = jobQuoteValidUntilText(status, validUntil)

  const timeline = jobQuoteTimeline(status, {
    sentAt: quote?.sentAt ?? lite.sentAt,
    viewedAt: quote?.viewedAt,
    acceptedAt: quote?.acceptedAt,
    rejectedAt: quote?.rejectedAt,
    validUntil,
    viewCount: views?.viewCount,
    lastViewedAt: views?.lastViewedAt,
  })

  const contact = inquiry?.contact
  const recipientEmail = contact?.email?.trim()
  const organizationId = inquiry?.organizationId || lite.organizationId
  const footer = jobQuoteTileFooter(status, {
    canRemind: Boolean(recipientEmail && organizationId),
    rejectionLabel: isRejected && quote ? getQuoteRejectionReasonLabel(quote.rejectionReasonCategory) : undefined,
  })
  const rejectionText = isRejected ? quote?.rejectionReason?.trim() : undefined

  const metaParts: ReactNode[] = []
  if (full?.isLoading) metaParts.push(<span key="loading" className="inline-block w-40 align-middle"><SkeletonBar className="h-4 w-40" /></span>)
  else if (full?.isError || !summary) metaParts.push(<span key="error">Couldn&apos;t load details</span>)
  else summary.tokens.forEach((token, index) => metaParts.push(<SummaryToken key={`token-${index}`} token={token} />))
  if (requiresQuestionnaire) metaParts.push(<span key="questionnaire">Questionnaire required</span>)
  if (validUntilText) metaParts.push(<span key="valid-until">{validUntilText}</span>)

  const quoteLink = { to: '/pipeline/$id/quotes/$quoteId' as const, params: { id: jobId, quoteId: lite.id } }

  return (
    <li className="flex min-w-0 flex-col gap-3 rounded-xl border bg-card p-4" aria-label={label}>
      <div className="flex items-start justify-between gap-2">
        <Link {...quoteLink} className="text-sm font-semibold hover:text-brand">
          {label}
        </Link>
        <QuoteStatusBadge status={status} label={jobQuoteTileStatusLabel(status)} />
      </div>

      <div className="leading-tight">
        <p className="font-financial text-2xl font-semibold">${formatCurrency(Number(totalCents))}</p>
        {withTip && <p className="mt-1 font-financial text-xs text-muted-foreground">{withTip}</p>}
      </div>

      {metaParts.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {metaParts.map((part, index) => (
            <span key={index}>
              {index > 0 && ' · '}
              {part}
            </span>
          ))}
        </p>
      )}

      {timeline.length > 0 && <QuoteTimeline label={label} steps={timeline} />}

      {rejectionText && <p className="break-words text-xs text-muted-foreground italic">&ldquo;{rejectionText}&rdquo;</p>}

      <div className="mt-auto flex items-center justify-between gap-3 border-t pt-3">
        <Link {...quoteLink} aria-label={`${footer.action} ${label}`} className="text-sm font-medium hover:text-brand">
          {footer.action} →
        </Link>
        {footer.hint?.kind === 'text' && (
          <span className="truncate text-xs text-muted-foreground">{footer.hint.text}</span>
        )}
        {footer.hint?.kind === 'reminder' && recipientEmail && (
          <QuoteReminderButton
            quoteId={lite.id}
            quoteLabel={label}
            organizationId={organizationId}
            recipientName={formatFullName(contact?.firstName, contact?.lastName, 'Unknown contact')}
            recipientEmail={recipientEmail}
          />
        )}
      </div>
    </li>
  )
}

interface AcceptedQuoteRowsProps {
  jobId: string
  accepted: QuoteLite
  notChosen: QuoteLite[]
  full: Record<string, QuoteByIdState>
}

/**
 * The Quotes card once a quote is accepted: compact rows, the accepted quote
 * first and the ones the client passed over greyed out under it. Each row
 * opens its quote on the job's quote page.
 */
function AcceptedQuoteRows({ jobId, accepted, notChosen, full }: AcceptedQuoteRowsProps) {
  return (
    <ul aria-label="Quotes" className="divide-y rounded-lg border">
      <CompactQuoteRow jobId={jobId} lite={accepted} full={full[accepted.id]} chosen />
      {notChosen.map((lite) => (
        <CompactQuoteRow key={lite.id} jobId={jobId} lite={lite} full={full[lite.id]} chosen={false} />
      ))}
    </ul>
  )
}

/** "Quote #N · Accepted/Not chosen · total · View →"; a not-chosen row reads greyed out. */
function CompactQuoteRow({
  jobId,
  lite,
  full,
  chosen,
}: {
  jobId: string
  lite: QuoteLite
  full: QuoteByIdState | undefined
  chosen: boolean
}) {
  const label = `Quote #${lite.quoteNumber}`
  const totalCents = full?.quote?.totalCents ?? lite.totalCents
  const quoteLink = { to: '/pipeline/$id/quotes/$quoteId' as const, params: { id: jobId, quoteId: lite.id } }
  const tone = chosen ? 'text-foreground' : 'text-muted-foreground'
  return (
    <li aria-label={label} className="flex items-center gap-3 px-4 py-2.5 text-sm">
      <Link {...quoteLink} className={cn('font-medium hover:text-brand', tone)}>
        {label}
      </Link>
      {chosen ? (
        <QuoteStatusBadge status={QuoteStatusEnum.QUOTE_STATUS_ACCEPTED} label="Accepted" />
      ) : (
        <Badge variant="secondary">Not chosen</Badge>
      )}
      <span className={cn('ml-auto font-financial', tone)}>${formatCurrency(Number(totalCents))}</span>
      <Link {...quoteLink} aria-label={`View ${label}`} className="shrink-0 text-xs text-muted-foreground hover:text-foreground">
        View →
      </Link>
    </li>
  )
}

const SENT_STATUSES = new Set<QuoteStatusEnum>([
  QuoteStatusEnum.QUOTE_STATUS_SENT,
  QuoteStatusEnum.QUOTE_STATUS_VIEWED,
  QuoteStatusEnum.QUOTE_STATUS_ACCEPTED,
  QuoteStatusEnum.QUOTE_STATUS_REJECTED,
  QuoteStatusEnum.QUOTE_STATUS_EXPIRED,
])

/** Whether the client could have opened the quote: it has a sent date or a status past sending. */
function wasSent(lite: QuoteLite): boolean {
  return Boolean(lite.sentAt) || SENT_STATUSES.has(lite.status)
}

interface JobQuotesHeaderProps {
  action?: ReactNode
  /** A plain header row with no card inset, for the booked job's cardless Quotes section. */
  plain?: boolean
  /** Makes the header the card's Show/Hide toggle (after acceptance), with these summary counts. */
  collapse?: { collapsed: boolean; onToggle: () => void; summaryItems: CardSummaryItem[] }
}

/**
 * The Quotes card header, matching the Original Inquiry and Events cards: a
 * 12px-padded row with the title left and the "+ New quote" menu right.
 * Before acceptance a divider sits under it. After acceptance the whole
 * header toggles the card like those cards do, and collapsed it carries
 * "1 accepted · 1 not chosen" beside the title.
 */
function JobQuotesHeader({ action, collapse, plain = false }: JobQuotesHeaderProps) {
  if (!collapse) {
    return (
      <CardHeader className="gap-0 border-b py-3 [.border-b]:pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="min-w-0 text-base">Quotes</CardTitle>
          {action}
        </div>
      </CardHeader>
    )
  }

  const { collapsed, onToggle, summaryItems } = collapse
  const Header = plain ? 'div' : CardHeader

  return (
    // The whole header toggles the card; the New quote menu and the chevron keep their clicks.
    <Header
      className={cn('cursor-pointer py-3 select-none', !plain && 'gap-0')}
      onClick={(event) => {
        if ((event.target as Element).closest('button, a, [role="menu"]')) return
        onToggle()
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 flex-1 items-baseline gap-x-3">
          <CardTitle className="shrink-0 text-base">Quotes</CardTitle>
          <CardSummaryItems show={collapsed} label="Quotes summary" items={summaryItems} />
        </div>
        <div className="-my-1 flex shrink-0 items-center gap-1">
          {action}
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Show quotes' : 'Hide quotes'}
            onClick={onToggle}
          >
            <CollapseChevron open={!collapsed} />
          </Button>
        </div>
      </div>
    </Header>
  )
}

/**
 * Quotes card on the pipeline job detail page: one tile per quote (newest
 * first) with its status, total, the events it proposes — quietly
 * highlighting where they differ from the request (the inquiry's events) —
 * its timeline with the client, and the next step. Once a quote is accepted
 * the card starts collapsed to "1 accepted · N not chosen"; expanded it lists
 * compact rows, the accepted quote first and the rest greyed as "Not chosen".
 * Every quote opens on the job's quote page (`/pipeline/$id/quotes/$quoteId`).
 * A job with no quotes shows a "No quote yet" card when it can start one
 * (`onDraftQuote` or `newQuoteAction`), and nothing otherwise.
 */
export function JobQuotes({
  jobId,
  quoteIds,
  acceptedQuoteId,
  inquiry,
  onDraftQuote,
  newQuoteAction,
  isBooked = false,
}: JobQuotesProps) {
  const { quotes, isLoading, isError } = useQuoteLitesByIds(quoteIds)
  // Fetched alongside the lites (not after them) — the tiles need each quote's events and dates.
  const fullQuotes = useQuotesByIds(quoteIds)
  // View counts come from the client's public-link interactions: one batched request for the sent quotes.
  const sentQuoteIds = useMemo(() => quotes.filter(wasSent).map((quote) => quote.id), [quotes])
  const { interactions } = useContactQuoteInteractionsByQuoteIds({
    contactId: inquiry?.contact?.id,
    quoteIds: sentQuoteIds,
    limitPerQuote: 100,
  })
  const viewSummaries = useMemo(() => jobQuoteViewSummaries(interactions), [interactions])
  // Only read once a quote is accepted or the job is booked: the card then starts collapsed to a one-line summary.
  const [collapsed, setCollapsed] = useState(true)
  const toggleCollapsed = () => setCollapsed((prev) => !prev)
  // Booked, the quotes are history: a section on the page background, like
  // the Original Inquiry above them, rather than a card.
  const Shell = isBooked ? 'section' : Card
  const shellProps = isBooked
    ? { 'aria-label': 'Quotes', className: 'flex flex-col' }
    : { className: 'gap-0 overflow-hidden pt-0' }
  const Body = isBooked ? 'div' : CardContent

  if (quoteIds.length === 0) {
    if (!onDraftQuote && !newQuoteAction) return null
    const empty = (
      <Body className="py-4">
          <div className="flex flex-col gap-3 rounded-lg border border-dashed px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium">No quote yet</p>
              <p className="text-xs text-muted-foreground">
                {onDraftQuote
                  ? 'Draft one from the inquiry; its events and add-ons carry over.'
                  : 'Start one from New quote.'}
              </p>
            </div>
            {onDraftQuote && (
              <Button variant="outline" size="sm" className="shrink-0" onClick={onDraftQuote}>
                <FilePlus className="h-4 w-4" />
                Draft quote
              </Button>
            )}
          </div>
      </Body>
    )
    if (isBooked) {
      return (
        <Shell {...shellProps}>
          <JobQuotesHeader
            action={newQuoteAction}
            plain={isBooked}
            collapse={{
              collapsed,
              onToggle: toggleCollapsed,
              summaryItems: [{ key: 'count', icon: FileText, label: 'No quotes' }],
            }}
          />
          <AnimatedCollapse open={!collapsed}>{empty}</AnimatedCollapse>
        </Shell>
      )
    }
    return (
      <Shell {...shellProps}>
        <JobQuotesHeader action={newQuoteAction} />
        {empty}
      </Shell>
    )
  }

  const sortedQuotes = jobDetailSortQuotes(quotes, acceptedQuoteId)
  const isAccepted = Boolean(acceptedQuoteId && quoteIds.includes(acceptedQuoteId))
  const body = isLoading ? (
    <div className="grid gap-4 sm:grid-cols-2">
      <SkeletonBar className="h-44 w-full rounded-xl" />
    </div>
  ) : isError ? (
    <p className="text-sm text-muted-foreground">Couldn&apos;t load quotes.</p>
  ) : null

  if (isAccepted) {
    const accepted = sortedQuotes.find((quote) => quote.id === acceptedQuoteId)
    const notChosen = sortedQuotes.filter((quote) => quote.id !== acceptedQuoteId)
    return (
      <Shell {...shellProps}>
        <JobQuotesHeader
          action={newQuoteAction}
          plain={isBooked}
          collapse={{
            collapsed,
            onToggle: toggleCollapsed,
            // From the job's ids, so the collapsed summary reads before the quotes load.
            summaryItems: acceptedSummary(quoteIds.filter((id) => id !== acceptedQuoteId).length),
          }}
        />
        {/* The divider under the header belongs to the collapsing body, so it leaves with it. */}
        <AnimatedCollapse open={!collapsed}>
          <Body className={cn('py-4', !isBooked && 'border-t')}>
            {body ??
              (accepted ? (
                <AcceptedQuoteRows jobId={jobId} accepted={accepted} notChosen={notChosen} full={fullQuotes} />
              ) : (
                <p className="text-sm text-muted-foreground">Couldn&apos;t load quotes.</p>
              ))}
          </Body>
        </AnimatedCollapse>
      </Shell>
    )
  }

  const tiles = body ?? (
    <ul aria-label="Quotes" className="grid gap-4 sm:grid-cols-2">
      {sortedQuotes.map((lite) => (
        <JobQuoteTile
          key={lite.id}
          jobId={jobId}
          lite={lite}
          full={fullQuotes[lite.id]}
          inquiry={inquiry}
          views={viewSummaries[lite.id]}
        />
      ))}
    </ul>
  )

  if (isBooked) {
    return (
      <Shell {...shellProps}>
        <JobQuotesHeader
          action={newQuoteAction}
          plain={isBooked}
          collapse={{
            collapsed,
            onToggle: toggleCollapsed,
            summaryItems: [
              { key: 'count', icon: FileText, label: `${quoteIds.length} ${quoteIds.length === 1 ? 'quote' : 'quotes'}` },
            ],
          }}
        />
        <AnimatedCollapse open={!collapsed}>
          <div className="flex flex-col gap-4 py-4">{tiles}</div>
        </AnimatedCollapse>
      </Shell>
    )
  }

  return (
    <Shell {...shellProps}>
      <JobQuotesHeader action={newQuoteAction} />
      <CardContent className="flex flex-col gap-4 py-4">{tiles}</CardContent>
    </Shell>
  )
}

/** "1 accepted · N not chosen", the collapsed summary once a quote is accepted. */
function acceptedSummary(notChosenCount: number): CardSummaryItem[] {
  const items: CardSummaryItem[] = [{ key: 'accepted', icon: CircleCheck, label: '1 accepted' }]
  if (notChosenCount > 0) items.push({ key: 'not-chosen', icon: CircleMinus, label: `${notChosenCount} not chosen` })
  return items
}
